import { useState, useCallback, useEffect } from "react";
import { DateInput, Button, OptionGroup, DataTable } from "@/components";
import { RightWrapper } from "../../../components";
import { BsFiletypeXls } from "react-icons/bs";
import ColumnHelper from "@/utils/ColumnHelper";
import { useMemo } from "react";
import { useFilteredData } from "@/";
import { getInvoiceData } from "../../../utils/Data";
import { HiOutlineDownload } from "react-icons/hi";
import { subDays } from "date-fns";
import { fetchInvoices } from "../../../app/slices";
import { useDispatch, useSelector } from "react-redux";
import { useApi } from "@";
const Invoice = () => {
  const currentDate = new Date();
  const startOfMonthDate = subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = currentDate;
  const options = [
    { label: "Bulk Invoice", value: "bulk_invoice", icon: <BsFiletypeXls /> },
  ];
  const [filters, setFilters] = useState({
    date: { from: startOfMonthDate, to: endOfMonthDate },
  });
  const handleOptionClick = (value) => {
    console.log("Option Selected:", value);
    if(value === "bulk_invoice") {
      handleDownload(selectedRows.map((row) => row._id));
    }
  };
  const [selectedRows, setSelectedRows] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [resetPageCounter, setResetPageCounter] = useState(0);
  const { callApi } = useApi();

  // Filter configuration
  const filterConfig = useMemo(
    () => ({
      date: { type: "date", itemKey: "invoiceDate" },
    }),
    []
  );
  const dispatch = useDispatch();
  const fetchInvoiceData = async () => {
    await dispatch(
      fetchInvoices({
        startDate: appliedFilters.date.from,
        endDate: appliedFilters.date.to,
      })
    );
  };

  // Fetch data when filters change
  useEffect(() => {
    fetchInvoiceData();
  }, [appliedFilters.date, dispatch]);

  // Selectors for Invoices
  const invoices = useSelector((state) => state.invoice.invoices) || [];
  const invoiceIsLoading = useSelector((state) => state.invoice.loading);

  // Memoize filtered data to prevent unnecessary re-renders
  const { data: finalFilteredData, isLoading } = useFilteredData(
    invoices,
    appliedFilters,
    filterConfig
  );
  const isInvoiceLoading = isLoading || invoiceIsLoading;
  const handleDownload = async (ids) => {
    const loadingIndicator = document.createElement("div");
    loadingIndicator.textContent = "Downloading invoice...";
    loadingIndicator.style.position = "fixed";
    loadingIndicator.style.bottom = "20px";
    loadingIndicator.style.right = "20px";
    loadingIndicator.style.backgroundColor = "#4CAF50";
    loadingIndicator.style.color = "white";
    loadingIndicator.style.padding = "10px";
    loadingIndicator.style.borderRadius = "5px";
    loadingIndicator.style.zIndex = "1000";
    document.body.appendChild(loadingIndicator);
    try {
      console.log("Downloading invoice:", ids);

      // Add loading indicator

      // Use the existing callApi hook with blob response type
      const response = await callApi(
        "/user/downloadInvoice",
        "POST",
        { ids },
        null,
        {
          Accept: "application/pdf",
        },
        "blob"
      );

      // Check if response is valid
      if (!response || response.size === 0) {
        console.error("Empty response received");
        document.body.removeChild(loadingIndicator);
        return;
      }

      // Create blob with proper MIME type
      const blob = new Blob([response], { type: "application/pdf" });

      // Create object URL
      const url = window.URL.createObjectURL(blob);

      // Create and trigger download
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice-${new Date().getTime()}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup with appropriate timeout
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        document.body.removeChild(loadingIndicator);
      }, 2000);
    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to download invoice. Please try again.");
      document.body.removeChild(loadingIndicator);
    }
  };
  // Format currency values
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  }, []);

  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);
  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Invoice ID",
        accessorKey: "invoiceNumber",
        cell: (info) => {
          const invNumber = info.row.original.invoiceNumber;
          const year = invNumber.substring(3, 5);
          const month = invNumber.substring(5, 7);
          const sequence = invNumber.substring(7);
          return `ICC/INV/${year}/${month}/${sequence}`;
        },
        meta: {
          tooltip: (row) => {
            const invNumber = row.invoiceNumber;
            if (!invNumber) return "N/A";
            const year = invNumber.substring(3, 5);
            const month = invNumber.substring(5, 7);
            const sequence = invNumber.substring(7);
            return `ICC/INV/${year}/${month}/${sequence}`;
          },
        },
      }),
      ColumnHelper({
        header: "Invoice Date",
        accessorKey: "invoiceDate",
        cell: (info) => formatDate(info.row.original.invoiceDate),
        meta: {
          tooltip: (row) => formatDate(row.invoiceDate),
        },
      }),
      ColumnHelper({
        header: "Status",
        accessorKey: "status",
        meta: {
          tooltip: (row) => row.status || "N/A",
        },
      }),
      ColumnHelper({
        header: "Invoice Amount",
        accessorKey: "totalAmount",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(info.row.original.totalAmount)}
          </span>
        ),
        meta: {
          tooltip: (row) => formatCurrency(row.totalAmount),
        },
      }),
      ColumnHelper({
        header: "Download",
        accessorKey: "_id",
        cell: (info) => (
          <div className="flex gap-1 justify-start items-center">
            <Button
              size="sm"
              className="!px-1.5 !py-1 !rounded-md "
              icon={HiOutlineDownload}
              onClick={() => handleDownload([info.row.original._id])}
            />
          </div>
        ),
        meta: {
          tooltip: () => "Download Invoice",
        },
      }),
    ],
    []
  );

  const handleDateChange = useCallback((dateRange) => {
    setFilters((prev) => ({
      ...prev,
      date: { from: dateRange?.from || "", to: dateRange?.to || "" },
    }));
  }, []);

  // Apply filters without resetting raw data
  const handleApply = useCallback(() => {
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  }, [filters]);

  // Reset filters properly
  const handleReset = useCallback(() => {
    const initialFilters = {
      date: { from: startOfMonthDate, to: endOfMonthDate },
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  }, []);
  return (
    <RightWrapper>
      <div className="flex flex-col gap-4 mt-2 px-2 relative pb-20 md:pb-4">
        {/* Filters Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <DateInput
            label="Date Filter"
            placeholder="Select From & To date"
            mode="range"
            value={filters.date}
            onChange={handleDateChange}
          />
          <div className="flex gap-2 justify-end md:justify-start">
            <Button variant="default" size="md" onClick={handleApply}>
              Apply
            </Button>
            <Button variant="outline" size="md" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </div>

        {/* Option Group Section */}
        {selectedRows.length > 0 && (
          <OptionGroup
            options={options}
            selectedCount={selectedRows.length}
            onClick={handleOptionClick}
          />
        )}
        <DataTable
          columns={columns}
          tittle="Invoice"
          data={finalFilteredData}
          isLoading={isInvoiceLoading}
          resetPage={resetPageCounter}
          getSelectedRows={setSelectedRows}
        />
      </div>
    </RightWrapper>
  );
};

export default Invoice;
